/**
 * SignalDetailModal
 * =================
 *
 * Click-through detail for a single signal, used by the Compact and
 * Rows view modes (the Default Grid view already renders the full
 * card inline so it doesn't need this).
 *
 * The modal renders the same ``SignalCard`` content as the default
 * Grid view but lets it expand to the full dialog width — a typical
 * desktop laptop ends up giving the chart 700–900 px of horizontal
 * room, vs ~280 px in the inline grid card. The card auto-resizes
 * because its mini chart is responsive.
 */

import React from 'react';
import {
  Box, Dialog, DialogContent, IconButton, Stack, Typography,
  alpha, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VerifiedIcon from '@mui/icons-material/Verified';

import SignalCard, { isEventLike } from './SignalCard';
import SignalDetailChart from './SignalDetailChart';
import EventStreamDetail from './EventStreamDetail';

const SignalDetailModal = ({
  open,
  signal,
  relevance,
  dataPriors,
  onClose,
  onDelete,
  onPublish,
  onUnsubscribe,
  onRename,
}) => {
  const theme = useTheme();
  if (!signal) return null;

  const isMarketplace = !!signal.marketplace;
  const displayKey = signal.display_name
    || signal.marketplace?.original_key || signal.key;

  // Wrap the close handlers so each action also dismisses the modal —
  // the same flow you'd expect from a "details" sheet (e.g. delete
  // a signal from the modal, modal closes, list re-renders without it).
  const wrap = (fn) => fn
    ? (...args) => { onClose && onClose(); fn(...args); }
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      {/* Slim header strip — keeps the dialog feeling like a "card
          detail" rather than a heavy modal. The signal key is the
          headline; the marketplace pill (when applicable) and a
          single close affordance live on the same line. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 2.5, py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.default,
                         theme.palette.mode === 'dark' ? 0.4 : 0.6),
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}
                    sx={{ flex: 1, minWidth: 0,
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis' }}>
          {displayKey}
        </Typography>
        {isMarketplace && (
          <Stack direction="row" spacing={0.5} alignItems="center"
                 sx={{ color: theme.palette.primary.main, fontSize: 12,
                       fontWeight: 600 }}>
            <StorefrontIcon sx={{ fontSize: 14 }} />
            <Box component="span">
              {signal.marketplace.seller_label || 'Marketplace'}
            </Box>
            {signal.marketplace.verified && (
              <VerifiedIcon sx={{ fontSize: 14 }} />
            )}
          </Stack>
        )}
        <IconButton size="small" onClick={onClose}
                    sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent sx={{
        p: 2.5,
        // Let the SignalCard's chart breathe — no scrollbars on
        // the dialog itself, just on the card content if needed.
        bgcolor: alpha(theme.palette.background.default,
                       theme.palette.mode === 'dark' ? 0.2 : 0.4),
      }}>
        {/* Event STREAMS get their own first-class detail: stats strip,
            score sparkline, and the actual event list (scores, values,
            scheduled badges) — the numeric chart/card make no sense for
            a stream of discrete occurrences. */}
        {isEventLike(signal) ? (
          <EventStreamDetail signal={signal} />
        ) : (
          <>
            {/* Re-use the production SignalCard untouched. We give it a
                big container; its inner ResponsiveLine + density strip
                scale to fill it. ``minHeight`` is bumped so the chart +
                stats + gauges have room to read at desktop size. */}
            {/* Full, scrollable history. Deliberately NO event-marker
                overlay here: this dialog is about ONE signal's own data,
                and workspace-wide event dots read as if they belonged to
                the signal. The Overview chart is where events + signals
                are meant to be seen together. */}
            <SignalDetailChart signal={signal} />
            <Box sx={{
              '& > .MuiPaper-root, & > .MuiCard-root': {
                minHeight: 520,
              },
            }}>
              <SignalCard
                signal={signal}
                relevance={relevance}
                dataPriors={dataPriors}
                onDelete={wrap(onDelete)}
                onPublish={wrap(onPublish)}
                onUnsubscribe={wrap(onUnsubscribe)}
                onRename={wrap(onRename)}
              />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SignalDetailModal;
